import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../api/supabase';
import { voidJournal } from '../../api/journal';
import { useAuth } from '../../context/AuthContext';

export default function JournalDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const username = user?.email?.split('@')[0] || 'unknown';
  const [je, setJe] = useState(null);
  const [lines, setLines] = useState([]);
  const [voidModal, setVoidModal] = useState(false);
  const [voidReason, setVoidReason] = useState('');

  useEffect(() => {
    supabase.from('journal_entries').select('*').eq('id', id).single().then(({ data }) => setJe(data));
    supabase.from('journal_lines').select('*, account:account_id(account_code, account_name)').eq('journal_entry_id', id).order('line_order')
      .then(({ data }) => setLines(data || []));
  }, [id]);

  const handleVoid = async () => {
    if (!voidReason) return;
    try {
      await voidJournal(id, voidReason, username);
      setVoidModal(false);
      // Reload
      supabase.from('journal_entries').select('*').eq('id', id).single().then(({ data }) => setJe(data));
    } catch (err) { alert(err.message); }
  };

  if (!je) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">{je.entry_number}</div>
          <div className="page-subtitle">{je.entry_date}  {je.description}</div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <span className={`badge ${je.status === 'POSTED' ? 'badge-green' : je.status === 'VOID' ? 'badge-red' : 'badge-yellow'}`} style={{ alignSelf: 'center', padding: '6px 14px' }}>{je.status}</span>
          {je.status === 'POSTED' && (
            <button className="btn btn-secondary" onClick={() => setVoidModal(true)}>Void Entry</button>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div className="card">
          {[['Entry No', je.entry_number], ['Date', je.entry_date], ['Status', je.status], ['Source', je.reference_type || (je.is_auto_posted ? 'Auto' : 'Manual')], ['Created By', je.created_by]].map(([l, v]) => v ? (
            <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
              <span style={{ color: 'var(--text-muted)' }}>{l}</span>
              <span style={{ fontWeight: 500 }}>{v}</span>
            </div>
          ) : null)}
        </div>
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
            <span style={{ color: 'var(--text-muted)' }}>Total Debit</span>
            <span className="amount" style={{ color: 'var(--danger)', fontWeight: 700 }}>{parseFloat(je.total_debit || 0).toLocaleString()}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
            <span style={{ color: 'var(--text-muted)' }}>Total Credit</span>
            <span className="amount" style={{ color: 'var(--success)', fontWeight: 700 }}>{parseFloat(je.total_credit || 0).toLocaleString()}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 13 }}>
            <span style={{ color: 'var(--text-muted)' }}>Balanced</span>
            <span style={{ fontWeight: 600, color: Math.abs(parseFloat(je.total_debit || 0) - parseFloat(je.total_credit || 0)) < 0.01 ? 'var(--success)' : 'var(--danger)' }}>
              {Math.abs(parseFloat(je.total_debit || 0) - parseFloat(je.total_credit || 0)) < 0.01 ? 'Yes' : 'No'}
            </span>
          </div>
          {je.void_reason && (
            <div style={{ marginTop: 12, padding: '8px 12px', background: 'rgba(239,68,68,0.06)', borderRadius: 6, fontSize: 12, color: 'var(--danger)' }}>
              Void reason: {je.void_reason} (by {je.voided_by})
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-title" style={{ marginBottom: 16 }}>Journal Lines</div>
        <div className="table-wrapper">
          <table>
            <thead><tr><th>Account Code</th><th>Account Name</th><th>Description</th><th style={{ textAlign: 'right' }}>Debit</th><th style={{ textAlign: 'right' }}>Credit</th></tr></thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={l.id}>
                  <td style={{ fontFamily: 'monospace', color: 'var(--text-muted)' }}>{l.account?.account_code || ''}</td>
                  <td style={{ fontWeight: 500 }}>{l.account?.account_name || ''}</td>
                  <td style={{ color: 'var(--text-muted)' }}>{l.description || ''}</td>
                  <td style={{ textAlign: 'right', color: 'var(--danger)' }} className="amount">{parseFloat(l.debit_amount || 0) > 0 ? parseFloat(l.debit_amount).toLocaleString() : ''}</td>
                  <td style={{ textAlign: 'right', color: 'var(--success)' }} className="amount">{parseFloat(l.credit_amount || 0) > 0 ? parseFloat(l.credit_amount).toLocaleString() : ''}</td>
                </tr>
              ))}
              <tr style={{ borderTop: '2px solid var(--border)', fontWeight: 700 }}>
                <td colSpan={3} style={{ textAlign: 'right', paddingRight: 12, color: 'var(--text-muted)', fontSize: 12 }}>TOTALS</td>
                <td style={{ textAlign: 'right', color: 'var(--danger)' }} className="amount">{lines.reduce((s, l) => s + parseFloat(l.debit_amount || 0), 0).toLocaleString()}</td>
                <td style={{ textAlign: 'right', color: 'var(--success)' }} className="amount">{lines.reduce((s, l) => s + parseFloat(l.credit_amount || 0), 0).toLocaleString()}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {voidModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="card" style={{ width: 420, padding: 24 }}>
            <div className="card-title" style={{ marginBottom: 12 }}>Void Journal Entry</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
              A reversal entry will be posted. Original entry will be marked VOID.
            </div>
            <div className="form-group" style={{ marginBottom: 16 }}>
              <label className="form-label">Void Reason *</label>
              <input className="form-input" value={voidReason} onChange={e => setVoidReason(e.target.value)} placeholder="Reason for voiding..." />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setVoidModal(false)}>Cancel</button>
              <button className="btn" style={{ flex: 1, background: 'var(--danger)', color: '#fff' }} onClick={handleVoid} disabled={!voidReason}>Confirm Void</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
